import { useQuery, useMutation } from "@apollo/client";
import queries from "Shared/queries";
import React from "react";
import mutations from "Shared/mutations";
import { successToast } from "Modules/DPToast";
import {
  EmployerNotificationsSettings,
  NotificationSettingsKeys,
  initialSubscriptionState,
} from "./EmailSubscriptionModels";
import {
  GetEmployerNotificationSettingsQuery,
  GetEmployerNotificationSettingsQueryVariables,
  ProviderPermissions,
  UpdateEmployerNotificationSettingsMutation,
  UpdateEmployerNotificationSettingsMutationVariables,
} from "generated/graphql";

type State = {
  checkboxes: null | EmployerNotificationsSettings;
  firstLoadCheckboxes: EmployerNotificationsSettings;
  isApplying: boolean;
};

export const useEmailSubscriptions = (permissions: ProviderPermissions, offCyclePaymentRequiresApproval = false) => {
  const [state, setState] = React.useState<State>({
    checkboxes: null,
    firstLoadCheckboxes: initialSubscriptionState,
    isApplying: false,
  });

  const { data, loading, error } = useQuery<
    GetEmployerNotificationSettingsQuery,
    GetEmployerNotificationSettingsQueryVariables
  >(queries.GET_EMPLOYER_NOTIFICATION_SETTINGS);

  const [updateSettingsFn] = useMutation<
    UpdateEmployerNotificationSettingsMutation,
    UpdateEmployerNotificationSettingsMutationVariables
  >(mutations.UPDATE_EMPLOYEE_NOTIFICATION_SETTINGS);

  React.useEffect(() => {
    if (!error && !loading && data && permissions) {
      const {
        actionRequiredEmailSubscribed,
        payrollJournalReminderSubscribed,
        earningsReminderSubscribed,
        offCycleDisbursementApprovalSubscribed,
      } = data.employer.employerNotificationSettings;

      const checkboxes: EmployerNotificationsSettings = {};
      if (permissions.canDownloadDirectDepositUpdates) {
        checkboxes[
          "actionRequiredEmailSubscribed"
        ] = actionRequiredEmailSubscribed;
      }
      if (permissions.canCreateNetEarnings) {
        checkboxes["earningsReminderSubscribed"] = earningsReminderSubscribed;
      }

      if (permissions.canCreateGrossEarnings) {
        checkboxes[
          "payrollJournalReminderSubscribed"
        ] = payrollJournalReminderSubscribed;
      }

      if (offCyclePaymentRequiresApproval && permissions.canCreateDisbursements) {
        checkboxes[
          "offCycleDisbursementApprovalSubscribed"
        ] = offCycleDisbursementApprovalSubscribed;
      }

      const firstLoadCheckboxes = Object.assign({}, checkboxes);

      setState({
        ...state,
        checkboxes,
        firstLoadCheckboxes,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, loading, data, permissions]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const newCheckboxes = Object.assign({}, state.checkboxes);
    newCheckboxes[event.target.value as NotificationSettingsKeys] = checked;
    setState({ ...state, checkboxes: newCheckboxes });
  };

  const clearSelection = () => {
    const newCheckboxes = Object.assign({}, state.checkboxes);
    Object.keys(newCheckboxes).forEach(key => {
      newCheckboxes[key as NotificationSettingsKeys] = false;
    });
    setState({ ...state, checkboxes: newCheckboxes });
  };

  const onAppyClickHandler = async () => {
    if (!state.checkboxes) {
      return;
    }

    const input = Object.keys(state.checkboxes).reduce(
      (acc, key) => {
        if (
          state.checkboxes &&
          state.checkboxes[key as NotificationSettingsKeys] !==
          state.firstLoadCheckboxes[key as NotificationSettingsKeys]
        ) {
          acc[key as NotificationSettingsKeys] =
            state.checkboxes[key as NotificationSettingsKeys];
        }

        return acc;
      },
      {} as EmployerNotificationsSettings
    );

    if (!Object.keys(input).length) {
      return;
    }

    const result = await updateSettingsFn({
      variables: { input },
    });

    if (result.data) {
      successToast(result.data.updateEmployerNotificationSettings.message);
    }
  };

  return {
    checkboxes: state.checkboxes,
    isApplying: state.isApplying,
    loading,
    error,
    handleChange,
    clearSelection,
    onAppyClickHandler,
  };
};
